// The bell — lives in the TopNav's right cluster next to the account menu.
// Unread count as a badge; click opens a compact inbox (latest rows), each
// row opens the work item in a tab and marks itself read. "See all" opens
// the Alerts page. Same dropdown grammar as the nav menus (TopNav.css).

import { useEffect, useRef, useState } from 'react';
import { useTab } from '@platform/app-shell/frontend/contexts/TabContext';
import useNotifications, { KIND_VERB, issueTabUrl, relativeTime } from '@modules/notifications/frontend/useNotifications';
import '@modules/notifications/frontend/components/NotificationBell/NotificationBell.css';

const PANEL_LIMIT = 8;

export function NotificationRow({ n, onOpen, compact = false }) {
  const verb = KIND_VERB[n.kind] || 'updated';
  return (
    <button
      type="button"
      className={`ntf-row ${n.read_at ? '' : 'ntf-row--unread'} ${compact ? 'ntf-row--compact' : ''}`}
      onClick={() => onOpen(n)}
      title={`${n.issue_key} — ${n.issue_summary || ''}`}
    >
      <span className={`ntf-kind ntf-kind--${n.kind}`} aria-hidden="true">{KIND_GLYPH[n.kind] || '•'}</span>
      <span className="ntf-body">
        <span className="ntf-line">
          <strong>{n.actor_name || 'Someone'}</strong> {verb} <span className="ntf-key">{n.issue_key}</span>
          {n.issue_summary ? <span className="ntf-summary"> — {n.issue_summary}</span> : null}
        </span>
        {n.detail ? <span className="ntf-detail">{n.detail}</span> : null}
        <span className="ntf-time">{relativeTime(n.created_at)}</span>
      </span>
      {!n.read_at && <span className="ntf-dot" aria-label="unread" />}
    </button>
  );
}

const KIND_GLYPH = { mention: '@', assigned: '👤', commented: '💬', transitioned: '→' };

export default function NotificationBell() {
  const { openTab } = useTab();
  const { items, unread, markRead, markAllRead } = useNotifications({ limit: PANEL_LIMIT });
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openItem = (n) => {
    setOpen(false);
    if (!n.read_at) markRead([n.id]);
    openTab(issueTabUrl(n.issue_key), n.issue_key);
  };
  const openAll = () => { setOpen(false); openTab('/alerts', 'Alerts'); };
  const badge = unread > 99 ? '99+' : String(unread);

  return (
    <div className="ntf-bell" ref={rootRef}>
      <button
        type="button"
        className={`ntf-bell-btn ${open ? 'ntf-bell-btn--open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={unread ? `Alerts, ${unread} unread` : 'Alerts'}
        title="Alerts"
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {unread > 0 && <span className="ntf-badge">{badge}</span>}
      </button>
      {open && (
        <div className="ntf-panel" role="menu">
          <div className="ntf-panel-head">
            <span className="ntf-panel-title">Alerts</span>
            {unread > 0 && (
              <button type="button" className="ntf-link" onClick={markAllRead}>Mark all read</button>
            )}
          </div>
          {items.length === 0
            ? <div className="ntf-empty">Nothing needs you right now.</div>
            : <div className="ntf-list">{items.map((n) => <NotificationRow key={n.id} n={n} onOpen={openItem} compact />)}</div>}
          <div className="ntf-panel-foot">
            <button type="button" className="ntf-link" onClick={openAll}>See all alerts</button>
          </div>
        </div>
      )}
    </div>
  );
}
